import { useEffect, useRef } from 'react';

import { CHART_COLORS } from '../constants';
import { formatCurrency } from '../formatters';
import { type AppAction, type CurrencyKey, type RoiResults } from '../types';
import styles from './EmailCapture.module.scss';
import HorizontalBarChart from './HorizontalBarChart';

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (config: Record<string, unknown>) => void;
      };
    };
    InteractRoiCalcData?: { ajaxUrl: string; nonce: string; themeUri: string };
  }
}

interface HubSpotConfig {
  portalId: string;
  formId: string;
  region: string;
  sfdcCampaignId: string;
}

/** Extract HubSpot config values from pasted embed code */
function parseHubSpotEmbed(embedCode: string): HubSpotConfig | null {
  const extract = (key: string): string => {
    const regex = new RegExp(`${key}\\s*:\\s*["']([^"']+)["']`);
    return embedCode.match(regex)?.[1] ?? '';
  };

  const formId = extract('formId');
  if (!formId) return null;

  return {
    portalId: extract('portalId'),
    formId,
    region: extract('region'),
    sfdcCampaignId: extract('sfdcCampaignId'),
  };
}

interface EmailCaptureProps {
  results: RoiResults;
  currency: CurrencyKey;
  hubspotEmbed: string;
  pricingPageUrl: string;
  dispatch: React.Dispatch<AppAction>;
}

function loadHubSpotScript(): Promise<void> {
  return new Promise((resolve) => {
    if (window.hbspt) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="hsforms"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      return;
    }
    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
}

export default function EmailCapture({
  results,
  currency,
  hubspotEmbed,
  pricingPageUrl,
  dispatch,
}: EmailCaptureProps) {
  const formContainerRef = useRef<HTMLDivElement>(null);

  const overallData = [
    {
      name: 'Productivity Yield',
      value: results.productivity.total.total,
      color: CHART_COLORS.plum,
    },
    {
      name: 'Onboarding Gains',
      value: results.onboarding.total.total,
      color: CHART_COLORS.orangeDark,
    },
    {
      name: 'Retention Returns',
      value: results.retention.total.total,
      color: CHART_COLORS.plumLight,
    },
  ];

  // Load HubSpot script and create form
  useEffect(() => {
    const config = parseHubSpotEmbed(hubspotEmbed);
    if (!config || !formContainerRef.current) return;

    const targetId = `roi-hubspot-form-${Date.now()}`;
    formContainerRef.current.id = targetId;

    loadHubSpotScript().then(() => {
      if (!window.hbspt) return;

      const createConfig: Record<string, unknown> = {
        portalId: config.portalId,
        formId: config.formId,
        target: `#${targetId}`,
        // Force inline message instead of server-side redirect
        inlineMessage: '<span></span>',
        onFormSubmitted: () => {
          dispatch({ type: 'SUBMIT_SUCCESS' });
          window.InteractTracking?.track('roi_calculator_submission', {
            page_name: document.title,
          });
        },
      };

      if (config.region) createConfig.region = config.region;
      if (config.sfdcCampaignId) createConfig.sfdcCampaignId = config.sfdcCampaignId;

      window.hbspt.forms.create(createConfig);
    });
  }, [hubspotEmbed, dispatch]);

  return (
    <div className={styles.emailCapture} id="report-start">
      <div className={styles.previewSection}>
        <div className={styles.previewText}>
          <span className={styles.previewLabel}>Total ROI over 3 years</span>
          <h3 className={styles.previewTotal}>
            {formatCurrency(results.totalRoi.total, currency)}
          </h3>
          <p className={styles.previewDesc}>
            The above figure shows the potential total savings split between Productivity Yield,
            Onboarding Savings, and Retention Returns. Compare this potential return with
            Interact&apos;s pricing.
          </p>
          {pricingPageUrl && (
            <a
              href={pricingPageUrl}
              className="interact-button button--primary-light button--medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="button__content">Get a quote now</span>
            </a>
          )}
        </div>
        <div className={styles.previewChart}>
          <HorizontalBarChart data={overallData} currency={currency} height={200} />
        </div>
      </div>

      <div className={styles.gateSection}>
        <div className={styles.formCard}>
          <div className={styles.formImageSide}>
            <img
              src={`${window.InteractRoiCalcData?.themeUri || ''}/assets/images/roi-report-mockup.png`}
              alt="Full report preview"
              className={styles.formImage}
            />
          </div>
          <div className={styles.formSide}>
            <h4 className={styles.formTitle}>
              View your
              <br />
              FREE Report now
            </h4>
            <div ref={formContainerRef} className={styles.hubspotForm} />
            <p className={styles.formDisclaimer}>
              Interact uses your information to contact you about our products. You can unsubscribe
              from our communications at any time.{' '}
              <a href="/terms/privacy-policy/">Privacy Policy</a>
            </p>
          </div>
        </div>

        <div className={styles.gateContent}>
          <h2 className={styles.gateHeading}>Introduction</h2>
          <p className={styles.gateText}>
            Your personalised report shows detailed cost savings across three key areas:
            Productivity Yield, Onboarding Gains, and Retention Returns. Each section breaks down
            the calculations year by year, showing how an intranet investment compounds over time.
          </p>
          <p className={styles.gateText}>
            The full report includes interactive charts, detailed data tables with calculation
            breakdowns, and a printable summary you can share with stakeholders.
          </p>
          <h3 className={styles.gateSubheading}>Productivity Yield</h3>
          <p className={styles.gateText}>
            Discover how much time your employees could save through improved communication,
            streamlined workflows, and faster access to information.
          </p>
          <h3 className={styles.gateSubheading}>Onboarding Gains</h3>
          <p className={styles.gateText}>
            See the potential savings from reducing new hire onboarding time and accelerating
            time-to-productivity for both desktop and frontline workers.
          </p>
        </div>
      </div>
    </div>
  );
}
